import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flag, AlertTriangle, MessageSquare, X, Check,
  Send, HelpCircle, AlertCircle, ShieldAlert
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/toast/use-toast';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.5, 
      ease: "easeOut" 
    } 
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3
    }
  }
};

const ReportForm = ({ 
  property,
  colors, // Recibe los mismos colores que el ContactSection para mantener consistencia
  onClose // Función para cerrar el formulario
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    tipo_reporte: '',
    descripcion: ''
  });

  // Validar que el usuario esté autenticado
  useEffect(() => {
    if (!user) {
      toast({
        title: "Debes iniciar sesión",
        description: "Para reportar una propiedad necesitas tener una cuenta",
        variant: "destructive",
      });
      onClose();
    }
  }, [user, onClose, toast]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
  
    // Validación de campos
    if (!formData.tipo_reporte) {
      toast({
        title: "Campo requerido",
        description: "Por favor, selecciona un motivo para el reporte",
        variant: "destructive",
      });
      return;
    }
  
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const reportPayload = {
        propiedad_id: property?.id,
        tipo_reporte: formData.tipo_reporte,
        descripcion: formData.descripcion
      };
  
      console.log('Report Payload:', reportPayload);
  
      const response = await fetch('http://localhost:3001/api/reportes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reportPayload)
      });
  
      const responseData = await response.json();
      console.log('Report Response:', {
        status: response.status,
        data: responseData
      });
  
      if (!response.ok) {
        throw new Error(
          responseData.error || 
          responseData.message || 
          'Error al enviar el reporte'
        );
      }
  
      setFormSubmitted(true);
      toast({
        title: "Reporte enviado",
        description: "Gracias por ayudarnos a mantener la plataforma segura",
        variant: "success",
      });
  
      setTimeout(() => {
        setFormSubmitted(false);
        onClose();
      }, 3000);
  
    } catch (error) {
      console.error('Error al enviar reporte:', error);
  
      toast({
        title: "Error al enviar reporte",
        description: error.message || "No se pudo enviar el reporte. Por favor, intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getReportTypeLabel = (type) => {
    switch(type) {
      case 'no_disponible':
        return 'Propiedad/es no disponible/s';
      case 'estafa':
        return 'Posible estafa';
      case 'otros':
        return 'Otros motivos';
      default:
        return 'Selecciona un motivo';
    }
  };

  // Iconos para cada tipo de reporte
  const getReportTypeIcon = (type) => {
    switch(type) {
      case 'no_disponible':
        return <X className="w-5 h-5" />;
      case 'estafa':
        return <AlertTriangle className="w-5 h-5" />;
      case 'otros':
        return <HelpCircle className="w-5 h-5" />;
      default:
        return <Flag className="w-5 h-5" />;
    }
  };

  // Clase para los botones de tipo de reporte
  const getReportTypeButtonClass = (type) => {
    const isSelected = formData.tipo_reporte === type;
    const baseClass = `
      flex items-center gap-2 p-3 rounded-lg border-2 
      transition-all duration-300 
      text-left flex-1 min-w-0
    `;
    
    if (isSelected) {
      return `${baseClass} ${colors.border} ${colors.light} font-semibold`;
    } else {
      return `${baseClass} border-gray-200 hover:border-gray-300 hover:bg-gray-50`;
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={fadeInUp}
      className="bg-white rounded-2xl shadow-xl p-6 relative overflow-hidden"
    >
      <AnimatePresence>
        {formSubmitted ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute inset-0 bg-green-500 z-20 flex flex-col items-center justify-center text-white"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1] }}
              transition={{ duration: 0.5, times: [0, 0.7, 1] }}
              className="bg-white/20 rounded-full p-4 mb-6"
            >
              <Check className="w-16 h-16" />
            </motion.div>
            <h3 className="text-2xl font-bold mb-2">¡Reporte Enviado!</h3>
            <p className="text-center px-4 text-white/90">Gracias por ayudarnos a mantener nuestra plataforma segura.</p>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="mb-5 flex justify-between items-center">
        <h2 className={`text-xl font-bold flex items-center gap-2 ${colors.text}`}>
          <ShieldAlert className="w-5 h-5" />
          Reportar propiedad
        </h2>
        <button 
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <div className="mb-4">
        <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-1">
          <AlertCircle className="w-4 h-4" />
          <p>¿Por qué estás reportando esta propiedad?</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-700 text-sm mb-4">
          Tu reporte es confidencial. Solo reporta si hay un problema real con esta publicación.
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Motivo del reporte *
          </label>
          <div className="grid grid-cols-1 gap-3">
            {['no_disponible', 'estafa', 'otros'].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, tipo_reporte: type }))}
                className={getReportTypeButtonClass(type)}
              >
                <span className={`${formData.tipo_reporte === type ? colors.text : 'text-gray-600'}`}>
                  {getReportTypeIcon(type)}
                </span>
                <span>{getReportTypeLabel(type)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Detalles adicionales
          </label>
          <div className="relative">
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-4 py-3 rounded-xl border-2 bg-gray-50 transition-all duration-300 
                ${formData.descripcion ? colors.border : 'border-gray-200'} 
                focus:outline-none focus:ring-2 focus:${colors.ring} focus:border-transparent`}
              placeholder="Describe el problema (opcional)"
            />
            <MessageSquare className={`absolute right-3 top-3 w-5 h-5 
                                  ${formData.descripcion ? colors.text : 'text-gray-400'}`} />
          </div>
          <p className="text-xs text-gray-500">
            Si necesitas adjuntar capturas o documentos, podrás hacerlo respondiendo al correo de confirmación.
          </p>
        </div>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          type="submit"
          disabled={loading || !formData.tipo_reporte}
          className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 mt-4
                   bg-gradient-to-r ${colors.button} text-white rounded-xl
                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:${colors.ring}
                   disabled:opacity-50 disabled:cursor-not-allowed 
                   shadow-lg transition-all duration-300 transform`}
        >
          {loading ? (
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ 
                repeat: Infinity, 
                duration: 1, 
                ease: "linear" 
              }}
              className="mr-2"
            >
              <AlertCircle className="w-5 h-5" />
            </motion.div>
          ) : (
            <Send className="w-5 h-5 mr-1" />
          )}
          {loading ? 'Enviando...' : 'Enviar Reporte'}
        </motion.button>
      </form>
    </motion.div>
  );
};

export default ReportForm;